// Global
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { Logo } from 'components/authorable/shared/site-structure/Header/HeaderDesktop';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import useClickOutside from 'lib/hooks/useClickOutside';
//import PreviewSearchBasicWidget from 'widgets/SearchPreview';
import { useOnRouteChange } from 'lib/hooks/useOnRouteChange';
import { SiteStructure } from '.generated/Feature.CW.model';
import { Data } from '.generated/Foundation.CW.model';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import classNames from 'classnames';
import { SearchOverlay } from './SearchOverlay';
import { useIsSafari } from 'lib/utils/useIsSafari';
import { useRouter } from 'next/router';
import { useSitecore } from '@sitecore-content-sdk/nextjs';

const HeaderMobile = (props: SiteStructure.Header.Header_Component) => {
  const { logo, logoLink } = props.fields || {};
  const { page } = useSitecore();
  const siteName = page?.siteName?.toLowerCase();
  const navigationList = props.fields
    ?.primaryNavigationList as SiteStructure.Header.PrimaryNavigationGroup_Item[];

  const utilityNavList = props?.fields
    ?.utilityNavigationList as SiteStructure.Header.UtilityNavigation_Item[];

  const headerRef = useRef<HTMLDivElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);

  /*
   * STATE
   */

  const [dropdownOpen, setDropdownOpen] = useState<null | number>(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [headerHeight, setHeaderHeight] = useState<number>(0);

  const isDropdownOpen = dropdownOpen !== null || showSearch || openMenu;

  const getHeaderHeight = () => {
    const header = document.getElementById('header-wrapper');
    return header ? header.offsetHeight : 0;
  };

  useEffect(() => {
    const getHeaderHeight = () => {
      const header = document.getElementById('header-wrapper');
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    // Delay slightly to ensure layout is rendered
    setTimeout(getHeaderHeight, 0);

    // Optional: Listen to resize
    window.addEventListener('resize', getHeaderHeight);

    return () => {
      window.removeEventListener('resize', getHeaderHeight);
    };
  }, [dropdownOpen]);

  /*
   * EVENT HANDLERS
   */

  const handleDropdownToggle = useCallback(
    (index: number | null) => {
      setDropdownOpen(dropdownOpen === index ? null : index);
    },
    [dropdownOpen]
  );

  const toggleHamburger = () => {
    const height = getHeaderHeight();
    setHeaderHeight(height);
    setOpenMenu(!openMenu);
    setDropdownOpen(null);
    setShowSearch(false);
  };

  const toggleSearch = () => {
    setOpenMenu(false);
    setShowSearch(!showSearch);
  };

  const closeAllMenus = useCallback(() => {
    handleDropdownToggle(null);
    setOpenMenu(false);
    setShowSearch(false);
  }, [handleDropdownToggle]);

  /*
   * LIFECYCLE
   */

  useUpdateMenuHeight(headerRef, menuContainerRef, isDropdownOpen);

  useClickOutside(headerRef, isDropdownOpen, closeAllMenus);

  useOnRouteChange(closeAllMenus);

  /*
   * Rendering
   */

  if (!props.fields) return <></>;

  const {
    base,
    //divider,
    hamburgerWrapper,
    headerSection,
    //languageSelection,
    languageWrapper,
    menuItems,
    searchWrapper,
    wrapper,
  } = TAILWIND_VARIANTS({ isOpen: openMenu, isSearch: showSearch });

  return (
    <>
      {openMenu && isDropdownOpen && <div className={base()}></div>}
      <div className={wrapper()} id="header-mobile" ref={headerRef}>
        {/* <div className={divider()}></div> */}
        <div className={headerSection()}>
          <Logo logo={logo} logoLink={logoLink} />
          {/* will be hidden in private - start */}
          <div className={hamburgerWrapper()}>
            <Hamburger
              openMenu={openMenu}
              toggleHamburger={toggleHamburger}
              toggleSearch={toggleSearch}
              showSearch={showSearch}
            />
          </div>
          {/* will be hidden in private - end */}
        </div>
        {/* will be hidden in private - start */}
        <div
          className={languageWrapper()}
          ref={menuContainerRef}
          style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
        >
          {showSearch && (
            <div className={searchWrapper()}>
              {/* <PreviewSearchBasicWidget defaultItemsPerPage={5} rfkId="rfkid_101" /> */}
              <SearchOverlay handleSearchToggle={() => setShowSearch(false)} />
            </div>
          )}
          {openMenu && (
            <div
              className="relative  overflow-y-auto"
              style={{ height: `calc(100dvh - ${headerHeight}px)` }}
            >
              {/* callout section start */}
              {props?.fields?.ctaLink?.value?.href && (
                <div className="rounded-md theme-aqua bg-theme-bg p-12 m-10">
                  <div className="text-16 text-gray-900 font-bold leading-[22px] font-centuryGothic">
                    <PlainTextWrapper field={props.fields?.cardText} />
                  </div>

                  <LinkWrapper
                    field={props?.fields?.ctaLink}
                    ctaVariant="tertiary"
                    className="!px-0 !pb-0 fill-theme-text !justify-start !gap-x-4"
                    ctaIcon="arrow-right"
                  />
                </div>
              )}
              {/* callout section end */}
              <div role="menu">
                {/* <ul role="presentation" className={menuItems()}>
                  {navigationList &&
                    navigationList.map((item, index) => (
                      <NavItem
                        {...item}
                        dropdownOpen={dropdownOpen}
                        index={index}
                        key={item.id}
                        onClick={() => handleDropdownToggle(index)}
                        headerHeightSize={headerHeight}
                      />
                    ))}
                </ul> */}
                <ul role="presentation" className={menuItems()}>
                  {navigationList &&
                    navigationList.map((item, index) => {
                      const isLastItem = index === navigationList.length - 1;

                      // If siteName is present and it's the last item -> render as CTA
                      if (siteName === 'giving' && isLastItem) {
                        return (
                          <li key={item.id} className="py-7 theme-lightblue">
                            <LinkWrapper
                              field={item.fields?.navigationLink}
                              ctaVariant="primary"
                              className="w-full flex justify-center"
                              role="link"
                            >
                              <PlainTextWrapper field={item.fields?.navigationTitle} />
                            </LinkWrapper>
                          </li>
                        );
                      }

                      // Default navigation rendering
                      return (
                        <NavItem
                          {...item}
                          dropdownOpen={dropdownOpen}
                          index={index}
                          key={item.id}
                          onClick={() => handleDropdownToggle(index)}
                          headerHeightSize={headerHeight}
                        />
                      );
                    })}
                </ul>
              </div>
              {/* utility nav links start*/}
              <div>
                <ul className="flex flex-col px-10 py-8">
                  {utilityNavList?.map((item: SiteStructure.Header.NavLinks, index: number) => {
                    return (
                      item?.fields?.navigationLink?.value.href && (
                        <li className="py-7" key={index}>
                          <LinkWrapper
                            className="font-centuryGothic text-14 font-bold text-blue-base leading-[19.6px] hover:underline hover:underline-offset-2 hover:text-darkblue-base"
                            field={item?.fields?.navigationLink}
                            role="link"
                            prefetch={false}
                          >
                            <PlainTextWrapper tag="span" field={item?.fields?.navigationTitle} />
                          </LinkWrapper>
                        </li>
                      )
                    );
                  })}
                </ul>
              </div>
              {/* utility nav links start*/}
              <div className="theme-lightblue px-10 py-16 mt-16 relative landscape:relative bottom-[calc(60px+env(safe-area-inset-bottom))] w-full">
                <LinkWrapper
                  ctaVariant="secondary"
                  field={props?.fields?.signIntoMyChart}
                  role="link"
                  prefetch={false}
                />
              </div>
            </div>
          )}
        </div>
        {/* will be hidden in private - end */}
      </div>
    </>
  );
};

const Hamburger = ({
  openMenu,
  toggleHamburger,
  toggleSearch,
  showSearch,
}: {
  openMenu: boolean;
  toggleHamburger: () => void;
  toggleSearch: () => void;
  showSearch: boolean;
}) => {
  const { hamburgerBase, hamburgerItem, hamburgerButton } = TAILWIND_VARIANTS();
  const router = useRouter();
  const { page } = useSitecore();
  const siteName = page.siteName?.toLowerCase();
  return (
    router.query.private !== 'true' && (
      <ul className={hamburgerBase()}>
        {showSearch && siteName !== 'giving' ? (
          <li className={hamburgerItem()}>
            <button
              aria-expanded={showSearch}
              aria-label="Toggle Menu"
              className={hamburgerButton()}
              onClick={() => toggleSearch()}
            >
              <SvgIcon fill="none" icon="close" size="lg" aria-label="close-menu" />
            </button>
          </li>
        ) : (
          !openMenu &&
          siteName !== 'giving' && (
            <li className={hamburgerItem()}>
              {/* Uncomment the line below to enable the search functionality */}
              <button
                aria-expanded={showSearch}
                aria-controls="mobile-menu-panel"
                aria-label="Toogle Search"
                className="hamburger-button h-[48px] w-[48px] cursor-pointer flex items-center justify-center search"
                onClick={() => toggleSearch()}
              >
                <SvgIcon
                  fill="none"
                  icon="search"
                  size="lg"
                  aria-label="Search"
                  className="fill-gray-900"
                />
              </button>
            </li>
          )
        )}

        {openMenu ? (
          <li className={hamburgerItem()}>
            <button
              aria-expanded={openMenu}
              aria-label="Toggle Menu"
              className={hamburgerButton()}
              onClick={() => toggleHamburger()}
            >
              <SvgIcon fill="none" icon="close" size="lg" aria-label="close-menu" />
            </button>
          </li>
        ) : (
          !showSearch && (
            <li className={hamburgerItem()}>
              <button
                aria-expanded={openMenu}
                aria-label="Toggle Menu"
                className={hamburgerButton()}
                onClick={() => toggleHamburger()}
              >
                <SvgIcon
                  fill="none"
                  icon="hamburger-menu"
                  size="xl"
                  aria-label="hamburger-menu"
                  viewBox="0 0 48 48"
                  className="fill-gray-900"
                />
              </button>
            </li>
          )
        )}
      </ul>
    )
  );
};

export interface NavItemProps extends SiteStructure.Header.PrimaryNavigationGroup_Item {
  dropdownOpen: number | null;
  index: number;
  onClick: () => void;
  headerHeightSize: number;
}

const NavItem = (props: NavItemProps) => {
  const megaMenuList = props.fields?.megaMenuList as SiteStructure.Header.MegaMenuGroup_Item[];
  const isList = (megaMenuList?.length ?? 0) > 0;

  const { menuNavItem, linkWrapper, dropDownButton, buttonItemIcon } = TAILWIND_VARIANTS({
    isRotated: isList && props.index === props.dropdownOpen,
  });

  return (
    <li role="presentation" className={menuNavItem()}>
      {!isList ? (
        <LinkWrapper
          aria-haspopup="false"
          className={linkWrapper()}
          field={props.fields?.navigationLink}
          role="menuitem"
          prefetch={false}
        >
          <PlainTextWrapper field={props.fields?.navigationTitle} />
        </LinkWrapper>
      ) : (
        <button
          aria-haspopup="true"
          aria-expanded={props.dropdownOpen === props.index}
          className={dropDownButton()}
          onClick={props.onClick}
          role="menuitem"
        >
          <span>{props.fields?.navigationTitle?.value}</span>
          <span className="flex">
            <SvgIcon
              className={buttonItemIcon()}
              fill="none"
              icon="chevron-left"
              size="md"
              viewBox="0 0 24 24"
            />
          </span>
        </button>
      )}

      <DropdownMenu
        className={`${isList && props.dropdownOpen === props.index ? 'right-0' : '-right-[100vw]'}`}
        categories={megaMenuList}
        categoryName={props.name}
        handleDropdown={props.onClick}
        headerHeightSize={props.headerHeightSize}
      />
    </li>
  );
};

const DropdownMenu = ({
  categories,
  categoryName,
  className,
  handleDropdown,
  headerHeightSize,
}: {
  categories: SiteStructure.Header.MegaMenuGroup_Item[];
  categoryName: string;
  className: string;
  handleDropdown: () => void;
  headerHeightSize: number;
}) => {
  const {
    dropDownMenuWrapper,
    dropDownMenuGroup,
    dropDownMenuLinkWrapper,
    dropDownMenuList,
    dropDownItemName,
    dropDownItem,
  } = TAILWIND_VARIANTS();
  const isSafari = useIsSafari();

  // const [headerHeight, setHeaderHeight] = useState<number>(0);

  // useEffect(() => {
  //   const getHeaderHeight = () => {
  //     const header = document.getElementById('header-wrapper');
  //     if (header) {
  //       setHeaderHeight(header.offsetHeight);
  //     }
  //   };

  //   // Delay slightly to ensure layout is rendered
  //   setTimeout(getHeaderHeight, 0);

  //   // Optional: Listen to resize
  //   window.addEventListener('resize', getHeaderHeight);

  //   return () => {
  //     window.removeEventListener('resize', getHeaderHeight);
  //   };
  // }, []);

  return (
    <div
      className={classNames(dropDownMenuWrapper(), className)}
      style={{ top: `${headerHeightSize}px`, height: `calc(100vh - ${headerHeightSize}px)` }}
    >
      <div
        className={`${isSafari ? 'relative' : 'fixed'} theme-lightblue py-4 border-b-gray-50 border-b-2 mb-12 bg-white w-full`}
        style={{ top: isSafari ? '0px' : `${headerHeightSize - 1}px` }}
      >
        <ButtonWrapper
          text={categoryName}
          ctaVariant="tertiary"
          ctaIconAlignment="left"
          ctaIcon="chevron-left"
          className="!px-8 flex [&>svg]:mb-[5px]"
          onClick={handleDropdown}
          aria-label="Back to Main Menu"
        />
      </div>
      <div
        className={`${isSafari ? 'pt-0' : 'pt-[94px]'} px-10  pb-[calc(120px+env(safe-area-inset-bottom))]`}
      >
        {categories.map((category, index) => {
          const megaMenuLinks = category.fields?.megaMenuLinks as Data.Links.GenericLink_Item[];
          return (
            <div
              aria-labelledby={`secondary-menu-${index + 1}`}
              className={dropDownMenuGroup()}
              key={category.id}
              role="group"
            >
              <h2 className={dropDownItemName()} id={`secondary-menu-${index + 1}`}>
                {category.name}
              </h2>
              <ul role="menu" className={dropDownMenuList()}>
                {megaMenuLinks?.map((item, i) => (
                  <li className={dropDownItem()} key={i}>
                    <LinkWrapper
                      className={classNames(dropDownMenuLinkWrapper(), '[&>svg]:hidden')}
                      field={item.fields?.link}
                      role="menuitem"
                      prefetch={false}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function useUpdateMenuHeight(
  headerRef: RefObject<HTMLDivElement | null>,
  menuContainerRef: RefObject<HTMLDivElement | null>,
  isDropdownOpen: boolean
) {
  useEffect(() => {
    const currentHeaderRef = headerRef.current;
    const currentMenuContainerRef = menuContainerRef.current;

    const updateMenuHeight = () => {
      if (currentHeaderRef && currentMenuContainerRef) {
        const totalMenuHeight =
          currentHeaderRef.offsetHeight + currentMenuContainerRef.offsetHeight;

        if (totalMenuHeight > window.innerHeight) {
          currentHeaderRef.style.height = '100%';
          currentHeaderRef.style.overflow = 'auto';
        } else {
          currentHeaderRef.style.height = 'initial';
          currentHeaderRef.style.overflow = 'initial';
        }
      }
    };

    const handleResize = () => {
      updateMenuHeight();
    };

    if (isDropdownOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed'; //Fixed position when dropdown is open
      updateMenuHeight();
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.style.overflow = 'initial';
      document.body.style.position = 'initial'; //Reset position when dropdown is closed
      if (currentHeaderRef) {
        currentHeaderRef.style.height = 'initial';
      }
    };
  }, [headerRef, menuContainerRef, isDropdownOpen]);
}

export default HeaderMobile;

const baseStateStyles = {
  false: {
    wrapper: ['shadow-default', 'print:!shadow-default'],
  },
  true: {
    wrapper: [],
    languageWrapper: ['right-[0]'],
  },
};

const TAILWIND_VARIANTS = tv({
  slots: {
    base: [
      'shadow-default',
      'print:!shadow-default',
      // 'before:backdrop-blur-sm',
      // 'before:bg-black/[0.5]',
      // 'before:fixed',
      // 'before:h-full',
      // 'before:left-0',
      // 'before:top-0',
      // 'before:w-full',
      // 'before:z-10',
    ],
    buttonItemIcon: ['duration-200', 'h-auto', 'transition', '!w-xs'],
    divider: ['bg-theme-black-800', 'h-3', 'w-full'],
    dropDownButton: [
      'cursor-pointer',
      'flex',
      'font-bold',
      'gap-2',
      'items-start',
      '!place-items-center',
      'text-18',
      'text-blue-base',
      'leading-[25px]',
      'font-centuryGothic',
      'w-full',
    ],
    dropDownItem: [],
    dropDownItemName: [
      'font-bold',
      'text-16',
      'font-centureyGothic',
      'text-gray-900',
      'pb-4',
      'mb-8',
      'border-b-2',
      'border-b-gray-50',
    ],
    dropDownMenuGroup: ['flex', 'flex-col', 'mb-8'],
    dropDownMenuLinkWrapper: [
      'font-centureyGothic',
      'text-gray-700',
      'hover:underline',
      'font-bold',
      'py-7',
      'text-14',
      'leading-[19.6px]',
    ],
    dropDownMenuList: ['flex', 'flex-col', 'gap-8'],
    dropDownMenuWrapper: ['bg-white', 'w-full', 'fixed', 'z-20', 'duration-500', 'overflow-y-auto'],
    hamburgerBase: ['flex', 'gap-2', 'h-full', 'items-center', 'justify-center', 'w-full'],
    hamburgerItem: ['flex', 'items-center', 'justify-center', 'mt-0', 'w-[48px]', 'h-[48px]'],
    hamburgerButton: [
      'hamburger-button',
      'h-[48px]',
      'w-[48px]',
      'flex',
      'items-center',
      'justify-center',
      'cursor-pointer',
      'transition-opacity',
      'duration-300',
    ],
    hamburgerWrapper: ['flex', 'items-center', 'print:hidden'],
    headerSection: ['flex', 'justify-between', 'px-10', 'py-12', 'md:px-[40px]', 'px-8'],
    languageSelection: ['bg-theme-black-300', 'p-4', 'rounded my-1'],
    languageWrapper: [
      'fixed',
      'bg-white',
      'border-b',
      'border-black',
      'w-full',
      '-right-[100vw]',
      'overflow-y-auto',
      // 'duration-500',
      'print:hidden',
      'z-50',
    ],
    linkWrapper: [
      'font-bold',
      'gap-2',
      '!place-items-center',
      'text-black',
      'text-18',
      'text-blue-base',
      'leading-[25px]',
      'font-centuryGothic',
      'mr-2',
      '[&>svg]:hidden',
    ],
    menuItems: ['flex', 'flex-col', 'm-0', 'px-10'],
    menuNavItem: ['group', 'list-none', 'm-0', 'relative', 'py-7', 'border-b', 'border-gray-50'],
    searchWrapper: ['bg-white', 'p-2', 'theme-white'],
    wrapper: ['bg-white', 'w-screen', 'z-40'],
  },
  variants: {
    isOpen: { ...baseStateStyles },
    isSearch: { ...baseStateStyles },
    isRotated: {
      false: {
        buttonItemIcon: ['rotate-180'],
      },
      true: {
        buttonItemIcon: ['rotate-180'],
      },
    },
  },
});
